import * as React from 'react';
import Table, { TableScrollWrapper } from './Table.tsx';
import classnames from '../utils/classnames.tsx';
import Checkbox from './Checkbox.tsx';
import { Line } from './Loader.tsx';
import { useControllableState } from '../utils/useControllableState.tsx';

export interface RecordTableColumn {
    key: string;
    label: string;
    render?: (record: Record) => React.ReactNode;
    className?: string;
}

interface Record {
    key: string;
    disabled?: boolean;
    [key: string]: any;
}

interface RecordTableProps {
    columns: RecordTableColumn[];
    records: Record[];
    selectable?: boolean;
    activeRowKey?: string;
    selectedRowKeys?: string[];
    highlightedRowKeys?: string[];
    onSelectedRowKeysChange?: (selectedRowKeys: string[]) => void;
    loading?: boolean;
    className?: string;
}

export const ROW_CONTROLS_KEY = '_rowControls';

const RecordCell = ({
    column,
    columnIndex,
    record,
    selectable,
    selectedRowKeys,
    highlightedRowKeys,
    handleSelectRow,
}: {
    column: RecordTableColumn;
    columnIndex: number;
    record: Record;
    selectable: boolean;
    selectedRowKeys: string[];
    highlightedRowKeys: string[];
    handleSelectRow: (key: string) => void;
}) => {
    return (
        <Table.Cell
            key={column.key}
            className={classnames(
                'group-hover:bg-surface-strong transition-colors duration-200',
                column.key === ROW_CONTROLS_KEY && 'pr-2',
                selectedRowKeys.includes(record.key) && 'bg-surface-brand-medium-default',
                highlightedRowKeys.includes(record.key) && 'bg-surface-brand-medium-default',
                column.className
            )}
            sticky={column.key === ROW_CONTROLS_KEY ? 'right' : undefined}
        >
            <div className="flex gap-3 items-center">
                {column.key !== ROW_CONTROLS_KEY && columnIndex === 0 && selectable && (
                    <Checkbox
                        size="sm"
                        checked={selectedRowKeys.includes(record.key)}
                        onChange={() => handleSelectRow(record.key)}
                    />
                )}
                <div
                    className={classnames(
                        'flex flex-1',
                        column.key === ROW_CONTROLS_KEY &&
                            'justify-end gap-1 group-hover:opacity-100 transition-opacity duration-200 [&:has(*[data-state=open])]:opacity-100'
                    )}
                >
                    {column.render ? column.render(record) : record[column.key]}
                </div>
            </div>
        </Table.Cell>
    );
};

const RecordTable = ({
    columns = [],
    records = [],
    selectable = true,
    selectedRowKeys: propsSelectedRowKeys,
    highlightedRowKeys = [],
    onSelectedRowKeysChange,
    loading = false,
    className,
}: RecordTableProps) => {
    const [selectedRowKeys, setSelectedRowKeys] = useControllableState(
        [],
        propsSelectedRowKeys,
        onSelectedRowKeysChange
    );

    const handleSelectAll = () => {
        if (selectedRowKeys.length === records.length) {
            setSelectedRowKeys?.([]);
        } else {
            setSelectedRowKeys?.(records.map((record) => record.key));
        }
    };

    const handleSelectRow = (key: string) => {
        if (selectedRowKeys.includes(key)) {
            setSelectedRowKeys?.(selectedRowKeys.filter((selectedKey) => selectedKey !== key));
        } else {
            setSelectedRowKeys?.([...selectedRowKeys, key]);
        }
    };

    const isAllSelected = selectedRowKeys.length === records.length && records.length > 0;
    const isIndeterminate = selectedRowKeys.length > 0 && selectedRowKeys.length < records.length;

    return (
        <TableScrollWrapper className={className}>
            <Table className="w-full border-none table-auto">
                <Table.Head sticky>
                    <Table.Row>
                        {columns.map((column, columnIndex) =>
                            column.key === ROW_CONTROLS_KEY ? (
                                <Table.HeadCell sticky="right" key={column.key} className={column.className} />
                            ) : (
                                <Table.HeadCell key={column.key} className={column.className}>
                                    <div className="flex gap-3 items-center">
                                        {columnIndex === 0 && selectable && (
                                            <Checkbox
                                                size="sm"
                                                indeterminate={isIndeterminate}
                                                checked={isAllSelected}
                                                onChange={handleSelectAll}
                                            />
                                        )}
                                        <div>{column.label}</div>
                                    </div>
                                </Table.HeadCell>
                            )
                        )}
                    </Table.Row>
                </Table.Head>
                <Table.Body>
                    {loading && records.length === 0 && (
                        <Table.Row>
                            {columns.map((column) => (
                                <Table.Cell key={column.key}>
                                    <div className="max-w-96 opacity-10">
                                        <Line />
                                    </div>
                                </Table.Cell>
                            ))}
                        </Table.Row>
                    )}
                    {records.map((record) => (
                        <Table.Row className={record.disabled ? 'opacity-50' : ''} key={record.key}>
                            {columns.map((column, columnIndex) => (
                                <RecordCell
                                    key={`${record.key}-${column.key}`}
                                    column={column}
                                    columnIndex={columnIndex}
                                    record={record}
                                    selectable={selectable}
                                    selectedRowKeys={selectedRowKeys}
                                    highlightedRowKeys={highlightedRowKeys}
                                    handleSelectRow={handleSelectRow}
                                />
                            ))}
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>
        </TableScrollWrapper>
    );
};

export default RecordTable;
